import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import arcaLogo from '../assets/arca_logo.png';
import PublicLayout from '../components/public/PublicLayout';

const ForceChangePassword = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const email = location.state?.email || '';

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match');
            return;
        }

        if (newPassword.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }

        if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(newPassword)) {
            toast.error('Password must contain uppercase, lowercase, number, and special character');
            return;
        }

        if (currentPassword === newPassword) {
            toast.error('New password must be different from current password');
            return;
        }

        setLoading(true);

        try {
            const API_BASE = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('userToken');

            const response = await fetch(`${API_BASE}/auth/force-change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ currentPassword, newPassword })
            });

            const data = await response.json();

            if (data.success) {
                toast.success('Password changed successfully!');
                navigate('/dashboard', { replace: true });
            } else {
                toast.error(data.message || 'Failed to change password');
            }
        } catch (error) {
            toast.error('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const renderPasswordInput = (id, value, onChange, placeholder, show, setShow, label) => (
        <div className="mb-3">
            <label htmlFor={id} className="form-label">{label}</label>
            <div className="position-relative">
                <input
                    type={show ? 'text' : 'password'}
                    className="form-control form-control-lg pe-5"
                    id={id}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    required
                    aria-label={label}
                />
                <button
                    type="button"
                    className="btn btn-link position-absolute top-50 end-0 translate-middle-y"
                    onClick={() => setShow(!show)}
                    style={{ textDecoration: 'none', color: '#6B8E23', paddingRight: '12px' }}
                    aria-label={show ? `Hide ${label}` : `Show ${label}`}
                >
                    {show ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                </button>
            </div>
        </div>
    );

    return (
        <PublicLayout>
            <div className="py-5" style={{ backgroundColor: '#ffffff', minHeight: '600px' }}>
                <div className="container d-flex align-items-center justify-content-center">
                    <div className="card shadow-lg border-0" style={{ maxWidth: '480px', width: '100%' }}>
                        <div className="card-body p-4 p-md-5">
                            <div className="text-center mb-4">
                                <img src={arcaLogo} alt="ARCA Logo" style={{ width: '150px', height: 'auto', marginBottom: '10px' }} />
                            </div>

                            <div className="alert alert-warning text-center" role="alert">
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                <strong>Password Change Required</strong>
                                <p className="mb-0 mt-1 small">Your password was reset by an administrator. Please set a new password to continue.</p>
                            </div>

                            {email && (
                                <p className="text-center text-muted mb-3">
                                    <i className="bi bi-envelope me-1"></i>{email}
                                </p>
                            )}

                            <form onSubmit={handleSubmit}>
                                {renderPasswordInput(
                                    'currentPassword',
                                    currentPassword,
                                    setCurrentPassword,
                                    'Enter your temporary password',
                                    showCurrentPassword,
                                    setShowCurrentPassword,
                                    'Current Password'
                                )}

                                {renderPasswordInput(
                                    'newPassword',
                                    newPassword,
                                    setNewPassword,
                                    'Min 8 chars, uppercase, lowercase, number, special',
                                    showNewPassword,
                                    setShowNewPassword,
                                    'New Password'
                                )}

                                {renderPasswordInput(
                                    'confirmPassword',
                                    confirmPassword,
                                    setConfirmPassword,
                                    'Re-enter new password',
                                    showConfirmPassword,
                                    setShowConfirmPassword,
                                    'Confirm New Password'
                                )}

                                <button
                                    type="submit"
                                    className="btn btn-success btn-lg w-100"
                                    style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Changing Password...
                                        </>
                                    ) : (
                                        'Change Password'
                                    )}
                                </button>
                            </form>

                            <div className="mt-3">
                                <small className="text-muted">
                                    Password must be at least 8 characters with at least one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&).
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </PublicLayout>
    );
};

export default ForceChangePassword;
